import React, { Suspense, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Canvas, extend, useThree, useFrame } from "react-three-fiber";
import { useGLTF, ContactShadows } from "drei";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls";
import * as THREE from "three";
import Modal from "react-bootstrap/Modal";
import { parseSRT, getCurrentSubtitle } from '../../../utils/srtParser';
import { fadeInPlay, fadeOutPause } from '../../../utils/audioFade';
import "./style.scss";

// Extend react-three-fiber with OrbitControls
extend({ OrbitControls: ThreeOrbitControls });

// Wrapper component for OrbitControls
function Controls() {
    const { camera, gl } = useThree();
    const controlsRef = useRef();

    useFrame(() => {
        if (controlsRef.current) {
            controlsRef.current.update();
        }
    });

    return (
        <orbitControls
            ref={controlsRef}
            args={[camera, gl.domElement]}
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
        />
    );
}

function Model3D({ url }) {
    const gltf = useGLTF(url);
    const scene = React.useMemo(() => gltf.scene.clone(true), [gltf.scene]);
    const groupRef = useRef();
    const { camera } = useThree();
    const rotationRef = useRef(0);

    // Auto-rotate the model
    useFrame((state, delta) => {
        if (groupRef.current && scene) {
            rotationRef.current += delta * 0.5; // Rotate at 0.5 radians per second
            scene.rotation.y = rotationRef.current;
        }
    });

    useEffect(() => {
        if (!scene) return;

        // Ensure the scene is properly set up
        scene.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                child.visible = true;
            }
        });

        // Use requestAnimationFrame to ensure the primitive is rendered first
        requestAnimationFrame(() => {
            try {
                // Calculate bounding box for camera positioning
                const box = new THREE.Box3().setFromObject(scene);
                const center = box.getCenter(new THREE.Vector3());
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);

                if (maxDim > 0 && isFinite(maxDim)) {
                    // Scale to fit in small window (make it 5.0 units in the largest dimension)
                    const scale = 5.0 / maxDim;
                    scene.scale.set(scale, scale, scale);

                    // Center the model
                    const centerCopy = center.clone();
                    scene.position.x = -centerCopy.x * scale;
                    scene.position.y = -centerCopy.y * scale;
                    scene.position.z = -centerCopy.z * scale;

                    // Position camera to view the centered model
                    const distance = Math.max(maxDim * scale * 1.3, 3);
                    camera.position.set(0, 0, distance);
                    camera.lookAt(0, 0, 0);
                    camera.updateProjectionMatrix();
                } else {
                    // Default: center position
                    scene.position.set(0, 0, 0);
                    camera.position.set(0, 0, 5);
                    camera.lookAt(0, 0, 0);
                    camera.updateProjectionMatrix();
                }
            } catch (error) {
                console.error('Model3D: Error positioning model:', error);
                scene.position.set(-0.5, 0, 0);
                camera.position.set(0.3, 0, 5);
                camera.lookAt(-0.5, 0, 0);
                camera.updateProjectionMatrix();
            }
        });
    }, [scene, camera, url]);

    if (!scene) return null;

    return <primitive ref={groupRef} object={scene} />;
}

function Model3DAudioModal({ show, onClose, modelUrl, audioSrc, srtSrc }) {
    const [isOpen, setIsOpen] = useState(show);
    const [isReady, setIsReady] = useState(false);

    // Audio state
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [subtitles, setSubtitles] = useState([]);
    const [currentSubtitle, setCurrentSubtitle] = useState(null);
    const [showSubtitles] = useState(true);

    useEffect(() => {
        setIsOpen(show);
        if (!show) {
            setIsReady(false);
            if (audioRef.current) {
                fadeOutPause(audioRef.current, 120, 1).finally(() => {
                    audioRef.current.currentTime = 0;
                    setIsPlaying(false);
                });
            }
        }
    }, [show]);

    // Load SRT file
    useEffect(() => {
        if (srtSrc && show) {
            fetch(srtSrc)
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                    return response.text();
                })
                .then(text => {
                    const parsed = parseSRT(text);
                    setSubtitles(parsed);
                })
                .catch(error => {
                    console.error('Error loading SRT file:', error);
                });
        }
    }, [srtSrc, show]);

    // Auto-play when modal opens and ready
    useEffect(() => {
        const audio = audioRef.current;
        if (show && isReady && audio) {
            const tryPlay = () => {
                if (audio && audio.readyState >= 2) {
                    fadeInPlay(audio, 140, 1)
                        .then((played) => {
                            if (played) setIsPlaying(true);
                        })
                        .catch(error => console.error('Auto-play failed:', error));
                }
            };

            const handleCanPlay = () => tryPlay();
            audio.addEventListener('canplay', handleCanPlay);
            const timeout = setTimeout(() => tryPlay(), 1000); // Slight delay to ensure UI is ready

            return () => {
                if (audio) {
                    audio.removeEventListener('canplay', handleCanPlay);
                }
                clearTimeout(timeout);
            };
        }
    }, [show, isReady]);

    // Update current subtitle based on playback time
    useEffect(() => {
        if (isPlaying) {
            const interval = setInterval(() => {
                if (audioRef.current) {
                    const time = audioRef.current.currentTime;
                    setCurrentTime(time);
                    if (subtitles.length > 0) {
                        const subtitle = getCurrentSubtitle(subtitles, time);
                        setCurrentSubtitle(subtitle);
                    }
                }
            }, 100);
            return () => clearInterval(interval);
        }
    }, [subtitles, isPlaying]);

    // Handle audio events
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateDuration = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        const handleTimeUpdate = () => {
            if (audio.currentTime !== undefined && isFinite(audio.currentTime)) {
                setCurrentTime(audio.currentTime);
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('loadeddata', updateDuration);
        audio.addEventListener('durationchange', updateDuration);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('loadeddata', updateDuration);
            audio.removeEventListener('durationchange', updateDuration);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [show]);

    const handleClose = () => {
        setIsOpen(false);
        setIsReady(false);
        if (onClose) onClose();
    };

    const togglePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                fadeOutPause(audioRef.current, 120, 1);
            } else {
                fadeInPlay(audioRef.current, 140, 1);
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleProgressChange = (e) => {
        if (audioRef.current && duration > 0) {
            const newTime = (parseFloat(e.target.value) / 100) * duration;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const handleProgressInput = (e) => {
        // Update in real-time while dragging
        if (audioRef.current && duration > 0) {
            const newTime = (parseFloat(e.target.value) / 100) * duration;
            setCurrentTime(newTime);
        }
    };

    const formatTime = (time) => {
        if (!isFinite(time)) return '0:00';
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (!modelUrl) return null;

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            dialogClassName="model-3d-audio-modal"
            onEntered={() => setIsReady(true)}
            backdrop={true}
            keyboard={true}
        >
            <Modal.Body className="model-3d-audio-modal-body">
                <div className="close-button" onClick={handleClose}>×</div>

                <div className="content-container">
                    {/* 3D Model Section */}
                    <div className="model-section">
                        {isReady && (
                            <Canvas
                                camera={{ position: [0, 0, 5], fov: 50 }}
                                gl={{
                                    antialias: true,
                                    shadowMap: { enabled: true },
                                    alpha: true,
                                    transparent: true
                                }}
                                onCreated={({ gl, scene, camera }) => {
                                    gl.shadowMap.enabled = true;
                                    gl.shadowMap.type = THREE.PCFSoftShadowMap;
                                    gl.setClearColor(0x000000, 0);
                                    scene.background = null;
                                }}
                            >
                                <ambientLight intensity={0.6} />
                                <directionalLight
                                    position={[5, 5, 5]}
                                    intensity={1.2}
                                    castShadow
                                />
                                <directionalLight position={[-5, 3, -5]} intensity={0.4} />
                                <pointLight position={[0, 5, 0]} intensity={0.8} distance={20} decay={2} />

                                <Suspense fallback={null}>
                                    <Model3D url={modelUrl} />
                                    <Controls />
                                    <ContactShadows
                                        rotation-x={Math.PI / 2}
                                        position={[0, -1, 0]}
                                        opacity={0.3}
                                        width={10}
                                        height={10}
                                        blur={2}
                                        far={1}
                                    />
                                </Suspense>
                            </Canvas>
                        )}
                        {!isReady && <div className="loading-text">Loading 3D Model...</div>}
                    </div>

                    {/* Audio Controls Section */}
                    <div className="audio-section">
                        <audio ref={audioRef} src={audioSrc} preload="auto" />

                        <div className="audio-controls">
                            <button className="play-pause-btn" onClick={togglePlayPause}>
                                {isPlaying ? '⏸' : '▶'}
                            </button>

                            <div className="progress-container">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={duration > 0 ? (currentTime / duration) * 100 : 0}
                                    onChange={handleProgressChange}
                                    onInput={handleProgressInput}
                                    className="progress-bar"
                                    style={{
                                        '--seek-before-width': duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                                    }}
                                />
                            </div>

                            <div className="time-display">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </div>
                        </div>

                        {/* Subtitles - Rendered via Portal to be at the bottom of the screen */}
                        {showSubtitles && currentSubtitle && ReactDOM.createPortal(
                            <div className="subtitle-display-fixed">
                                {currentSubtitle.text}
                            </div>,
                            document.body
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default Model3DAudioModal;
